/* eslint-disable max-lines */
import { skipToken } from '@reduxjs/toolkit/query';
import { useDebouncedFunction } from '@strata/helper-functions';
import { ChatWithConversationPane } from '@strata/react-chat';
import { Spinner } from '@strata/react-loading-animation';
import { SuccessToast } from '@strata/react-toast-message';
import { useFlags } from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ChangeConversationAssignmentDialog } from 'components/referrals/dialogs/ChangeConversationAssignmentDialog';
import { ConversationDialog } from 'components/referrals/dialogs/ConversationDialog';
import { useUserProfile, useUserSupportMessages } from 'services/APIHooks';
import { handleError } from 'services/Error';
import { AppRoutes } from 'services/routes';
import {
    ConversationActions,
    ConversationStatusChangeActionMap,
    detailFactsSort,
    getApiBodyFactsReducer,
    getDetailFactsReducer,
} from 'services/userSupportHelpers';
import { useRedirectedConversation } from 'services/userSupportHooks';
import { store } from 'store';
import {
    TagTypes,
    usePostFillActivityRequestMutation,
    userSupportApi,
} from 'store/api/userSupportApi';
import {
    useCreateConversationMutation,
    useSearchReferralConversationsQuery,
    useUpdateConversationFactsMutation,
} from 'store/api/userSupportChatApi';
import { updateCurrentConversation } from 'store/userSupport/userSupportSlice';

// A skipped query keeps returning its last result, so drop it once the search is cleared
const activeSearchResults = (searchTerm, searchData) => (searchTerm ? searchData : undefined);

export const Messages = ({
    referralId,
    referralFriendlyId,
    memberFirstName,
    memberLastName,
    supplierNpi,
    segmentId,
    benefit,
    conversations = undefined,
    performanceEntries = undefined,
    conversationDialogAction = undefined,
    setConversationDialogAction,
}) => {
    const dispatch = useDispatch();
    const selectedConversationId = useSelector((state) => state.userSupport.currentConversation);
    const {
        messages,
        sendMessage,
        facts,
        settledFacts,
        updateConversationStatus,
        updateConversationAssignee,
        writesViaRest,
        fetchViaRest,
    } = useUserSupportMessages();
    const [chatLoading, setChatLoading] = useState(false);
    const [changeAssigneeForConversation, setChangeAssigneeForConversation] = useState();
    const { userProfile } = useUserProfile();
    const [searchTerm, setSearchTerm] = useState();
    const debouncedSearch = useDebouncedFunction();
    const { sendFillActivityRequest } = useFlags();

    const [postFillActivityRequest] = usePostFillActivityRequestMutation();
    const [createConversation] = useCreateConversationMutation();
    const [updateConversationFacts] = useUpdateConversationFactsMutation();
    const { data: searchData } = useSearchReferralConversationsQuery(
        searchTerm ? { referralId, searchTerm } : skipToken,
    );
    const searchResults = activeSearchResults(searchTerm, searchData);

    const selectedConversation = useMemo(
        () => conversations?.find((conv) => conv.conversationId === selectedConversationId),
        [conversations, selectedConversationId],
    );

    // DEV-37337: cached conversation facts can hold datum_ids the API has
    // already deleted (another user changed the topic), and submitting those
    // wipes the conversation's facts. Refetch when the change dialog opens, and
    // withhold facts from it until the refetch lands (currentFacts below).
    useEffect(() => {
        if (conversationDialogAction === ConversationActions.CHANGE && selectedConversationId) {
            dispatch(
                userSupportApi.util.invalidateTags([
                    { type: TagTypes.CONVERSATION_FACTS, id: selectedConversationId },
                ]),
            );
        }
    }, [conversationDialogAction, dispatch, selectedConversationId]);

    useRedirectedConversation(conversations);

    const canCallSocket = (socketCall) => {
        const { userSupport } = store.getState();
        const { socketConnected } = userSupport;
        if (socketConnected) {
            socketCall();
            return true;
        }
        return false;
    };

    const makeSocketCall = async (socketCall) => {
        if (canCallSocket(socketCall)) {
            return Promise.resolve(true);
        }
        setChatLoading(true);
        return new Promise((resolve) => {
            const timer = setInterval(() => {
                if (canCallSocket(socketCall)) {
                    clearInterval(timer);
                    setChatLoading(false);
                    resolve(true);
                }
            }, 5000);
        });
    };

    // Writes go over REST when the user-support-rest-writes flag is on and no
    // longer need to wait for the socket connection.
    const makeUserSupportWrite = (call) => (writesViaRest ? call() : makeSocketCall(call));

    // With REST fetch on, selecting a conversation triggers a fetch instead of
    // a socket subscription, so the socket-connected wait is unnecessary.
    const selectConversation = (conversationId) =>
        fetchViaRest
            ? dispatch(updateCurrentConversation({ conversationId }))
            : makeSocketCall(() => dispatch(updateCurrentConversation({ conversationId })));

    if (!referralId || !conversations || !userProfile) {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    height: '100%',
                }}
            >
                <Spinner size={Spinner.Size.LARGE} intent={Spinner.Intent.NONE} />
            </div>
        );
    }

    const conversationsList = searchResults ?? conversations ?? [];

    return (
        <>
            <ConversationDialog
                action={conversationDialogAction}
                performanceEntries={performanceEntries}
                onCancel={setConversationDialogAction}
                onCreate={async ({ topic, facts: newFacts, details }) => {
                    if (!selectedConversation) {
                        setChatLoading(true);
                    }
                    try {
                        const newConversation = await createConversation({
                            referralId,
                            body: {
                                details: {
                                    created_by_upn: userProfile.upn,
                                    details,
                                    referral_friendly_id: referralFriendlyId,
                                    member_first_name: memberFirstName,
                                    member_last_name: memberLastName,
                                    supplier_npi: supplierNpi,
                                    segment_id: segmentId,
                                    benefit,
                                    topic_id: topic.value,
                                },
                                data: Object.values(newFacts).reduce(
                                    ...getApiBodyFactsReducer(newFacts, details),
                                ),
                            },
                        }).unwrap();
                        setConversationDialogAction();
                        await selectConversation(newConversation.conversationId);
                        SuccessToast({ message: `New conversation created: ${topic.name}.` });
                    } catch (error) {
                        handleError('Unable to create a new conversation.', error);
                        throw error;
                    }
                    setChatLoading(false);
                }}
                onChange={async ({ topic, facts: newFacts, details }) => {
                    if (!selectedConversation) {
                        setChatLoading(true);
                    }
                    try {
                        await updateConversationFacts({
                            conversationId: selectedConversationId,
                            body: {
                                details,
                                topic_id: topic.value,
                                data: Object.values(newFacts).reduce(
                                    ...getApiBodyFactsReducer(newFacts, details, true),
                                ),
                            },
                        }).unwrap();
                        SuccessToast({ message: `Conversation type changed: ${topic.name}.` });
                        setConversationDialogAction();
                    } catch (error) {
                        handleError('Unable to change conversation type.', error);
                        throw error;
                    }
                    setChatLoading(false);
                }}
                currentFacts={settledFacts}
                currentTopic={selectedConversation?.topicId}
            />
            <ChangeConversationAssignmentDialog
                isOpen={!!changeAssigneeForConversation}
                currentAssigneeUpn={changeAssigneeForConversation?.assignedUpn}
                onChangeAssignment={async ({ assignee }) => {
                    try {
                        await makeUserSupportWrite(() =>
                            updateConversationAssignee({
                                conversation_id: changeAssigneeForConversation?.conversationId,
                                assignee_upn: assignee.upn,
                            }),
                        );
                        setChangeAssigneeForConversation();
                        SuccessToast({ message: `Conversation assigned successfully.` });
                    } catch (error) {
                        handleError('Unable to change assignment.', error);
                    }
                }}
                onCancel={setChangeAssigneeForConversation}
            />
            <ChatWithConversationPane
                conversations={conversationsList}
                showViewDetailsButton
                userUPN={userProfile.upn}
                isSupportUser
                chatLinkGenerator={(conversation) =>
                    conversation?.displayId
                        ? `${window.location.origin}${AppRoutes.conversationDetails(
                              conversation?.displayId,
                          )}`
                        : undefined
                }
                conversationType={ChatWithConversationPane.ConversationType.CHAT}
                onSearch={(value) => {
                    if (value?.length > 0) {
                        debouncedSearch(() => setSearchTerm(value));
                    } else {
                        setSearchTerm();
                    }
                }}
                onConversationSelect={async (conversationId) => {
                    await selectConversation(conversationId);
                }}
                selectedConversation={
                    selectedConversation
                        ? {
                              ...selectedConversation,
                              messages: Object.values(messages ?? {}),
                              details: {
                                  ...selectedConversation.details,
                                  data: [...(facts ?? [])]
                                      .sort(detailFactsSort)
                                      .reduce(...getDetailFactsReducer(selectedConversation)),
                              },
                          }
                        : undefined
                }
                onNewConversationClick={
                    supplierNpi
                        ? () => setConversationDialogAction(ConversationActions.CREATE)
                        : undefined
                }
                onChangeConversationType={() =>
                    setConversationDialogAction(ConversationActions.CHANGE)
                }
                onConversationStatusChange={async (conversationId, action) => {
                    const { newStatus, actionWord } = ConversationStatusChangeActionMap[action];
                    try {
                        await makeUserSupportWrite(() =>
                            updateConversationStatus({
                                conversation_id: conversationId,
                                new_status: newStatus,
                            }),
                        );
                        SuccessToast({ message: `Conversation ${actionWord} successfully.` });
                    } catch (error) {
                        handleError('Unable to update conversation.', error);
                    }
                }}
                onChangeAssignment={(conversationId, assignedUpn) =>
                    setChangeAssigneeForConversation({ conversationId, assignedUpn })
                }
                onMessageSend={async ({ conversationId, details }) => {
                    try {
                        await makeUserSupportWrite(() =>
                            sendMessage({
                                conversation_id: conversationId,
                                details: {
                                    content: {
                                        message: details.message,
                                    },
                                },
                            }),
                        );
                        SuccessToast({ message: 'Message sent successfully.' });
                    } catch (error) {
                        handleError('Unable to save message.', error);
                    }
                }}
                onSendFillActivityRequest={
                    sendFillActivityRequest
                        ? (conversationId) => {
                              try {
                                  postFillActivityRequest({ conversationId }).unwrap();
                                  SuccessToast({
                                      message: 'Fill activity request sent successfully.',
                                  });
                              } catch (error) {
                                  handleError('Unable to send fill activity request.', error);
                              }
                          }
                        : undefined
                }
                highlightText={searchTerm}
                chatLoading={chatLoading || !messages || !facts}
            />
        </>
    );
};

Messages.propTypes = {
    referralId: PropTypes.string.isRequired,
    referralFriendlyId: PropTypes.string.isRequired,
    memberFirstName: PropTypes.string.isRequired,
    memberLastName: PropTypes.string.isRequired,
    supplierNpi: PropTypes.string.isRequired,
    segmentId: PropTypes.string.isRequired,
    benefit: PropTypes.string.isRequired,
    conversations: PropTypes.arrayOf(
        PropTypes.shape({
            conversationId: PropTypes.string,
        }),
    ),
    performanceEntries: PropTypes.arrayOf(PropTypes.shape({})),
    conversationDialogAction: PropTypes.oneOf(Object.values(ConversationActions)),
    setConversationDialogAction: PropTypes.func.isRequired,
};
